/**
 * Source-side URL construction for the SharePoint CreateCopyJobs API.
 *
 * CreateCopyJobs needs every source file as an absolute URL. Those URLs are built
 * from each row's OWN server-relative `FileRef`, anchored to the source site's
 * origin — the source *folder* path plays no part in it.
 *
 * This module exists because the previous inline version anchored on
 * `origin + sourceServerRelPath`, which folds a caller-supplied fragment into the
 * URL's AUTHORITY whenever it lacks a leading slash:
 *
 *     "https://host" + "/sites/x/Shared Documents"  -> path; the space is encoded, fine
 *     "https://host" + "All Documents"              -> authority; the space is illegal, THROWS
 *     "https://host" + "AllDocuments"               -> authority; parses to the WRONG host
 *
 * The all-documents aggregate view passed a localized display label in that field,
 * so whether a copy/move crashed, silently targeted a non-existent host, or worked
 * depended on whether a translated UI string happened to contain a space. Anchoring
 * on `sourceSiteUrl` alone removes that entire class of failure: no caller-supplied
 * value can reach the authority any more.
 *
 * Behaviour is unchanged for every legitimate caller — a server-relative source path
 * on the same host never altered the origin it was concatenated onto.
 */

export interface ICopyJobSourcePath {
    /** Absolute, percent-encoded URL of the file itself (`exportObjectUris` entry). */
    filePath: string;
    /** Its parent folder URL. Jobs are grouped by this, one per source folder. */
    containerPath: string;
}

export interface ICopyJobSourcePathsResult {
    paths: ICopyJobSourcePath[];
    /**
     * Rows whose `FileRef` was missing or could not be turned into a URL. Callers
     * must treat a non-empty list as a hard failure: dropping them would silently
     * copy a subset of what the user selected.
     */
    unresolved: any[];
}

/**
 * Server-relative folder path, normalized for comparison: decoded, leading slash, no
 * trailing slash, lower-cased. SharePoint paths are case-insensitive, so a case
 * difference between the destination and a row's FileRef is the SAME folder.
 *
 * Decoding matters because the two sides arrive differently encoded: `FileRef` comes
 * back from RenderListDataAsStream already decoded, while several drag-and-drop callers
 * derive the destination from `webUrl`, which is percent-encoded. Without this, any
 * folder with a space in its name silently failed to match. Decoding cannot cause a
 * FALSE match — `/A%20B` and `/A B` are the same folder — and a malformed sequence
 * (a literal `%` in a name) falls back to the raw value rather than throwing.
 */
const normalizeFolderPath = (path?: string): string => {
    const trimmed = String(path ?? "").trim();
    if (!trimmed) {
        return "";
    }
    let decoded: string;
    try {
        decoded = decodeURIComponent(trimmed);
    } catch {
        decoded = trimmed;
    }
    const withLeadingSlash = decoded.startsWith("/") ? decoded : `/${decoded}`;
    const withoutTrailingSlash = withLeadingSlash.length > 1 && withLeadingSlash.endsWith("/")
        ? withLeadingSlash.slice(0, -1)
        : withLeadingSlash;
    return withoutTrailingSlash.toLowerCase();
};

/**
 * Whether any queued row already lives in `destinationServerRelPath`.
 *
 * Moving an item into the folder it already occupies is degenerate — source and
 * destination resolve to the same URL — and CreateCopyJobs rejects it. The two existing
 * guards (the tray's `sameAsSource` blocker and the commit dialog's render condition)
 * both compare the destination against `moveTo.workingDirectories`' last entry, which
 * is the source FOLDER only for a library-initiated operation. An aggregate selection
 * spans libraries, so that entry is the site, and neither guard can see the real source
 * of any individual row.
 *
 * Compared at FOLDER granularity, not by library: the aggregate view queries with
 * Scope="RecursiveAll", so its rows come from every folder level, and a move from
 * `LibA/Sub1` into `LibA` is perfectly legitimate.
 *
 * Both sides are raw server-relative paths here, so there is no encoding asymmetry to
 * defeat the comparison (`destinationPath` elsewhere in createCopyJobs is unencoded
 * while `filePath` is percent-encoded — do not compare those two).
 */
export const destinationHoldsQueuedRows = (destinationServerRelPath: string, files: any[]): boolean => {
    const destination = normalizeFolderPath(destinationServerRelPath);
    if (!destination) {
        return false;
    }

    // `some`, not `filter`: neither caller wants the rows, and the case that matters —
    // the user standing in the folder the selection came from — settles on the first one
    // instead of normalizing the whole queue.
    return (files ?? []).some(file => {
        // Read the field ONCE — these rows come straight from the grid and nothing
        // guarantees FileRef is a plain data property.
        const rawFileRef = file?.FileRef;
        const fileRef: string = typeof rawFileRef === "string" ? rawFileRef.trim() : "";
        if (!fileRef) {
            return false;
        }
        const withLeadingSlash = fileRef.startsWith("/") ? fileRef : `/${fileRef}`;
        const lastSlashIndex = withLeadingSlash.lastIndexOf("/");
        // <= 0 means the path is a bare filename at the root — never a real library folder.
        if (lastSlashIndex <= 0) {
            return false;
        }
        return normalizeFolderPath(withLeadingSlash.slice(0, lastSlashIndex)) === destination;
    });
};

/**
 * The characters `new URL` cannot be trusted to carry through a path, mapped to their
 * escapes.
 *
 * `FileRef` arrives DECODED, so each of these is a literal character in a real file or
 * folder name — but handed to `new URL` unescaped, `#` starts a fragment and `?` starts a
 * query, silently truncating the path, while a stray `%` forms an invalid escape.
 * `exportObjectUris` then names a file that does not exist, or a different one.
 *
 * Everything else is left to `new URL`, so any path without these characters produces a
 * byte-identical result to before this existed — which is the point: the encoding of
 * every currently-working move is untouched.
 */
const URL_UNSAFE_PATH_CHARS: { [char: string]: string } = { "%": "%25", "#": "%23", "?": "%3F" };

/**
 * SOURCE-side escaping, for paths known to be decoded (`FileRef`).
 *
 * One pass, so an escape this function introduces can never be re-escaped by it — the
 * `%` of a `%23` is never revisited. Sequential replaces would double-encode.
 */
export const escapeUrlUnsafePathChars = (path: string): string =>
    path.replace(/[%#?]/g, char => URL_UNSAFE_PATH_CHARS[char]);

/**
 * DESTINATION-side escaping. Same intent, minus `%` — and that omission is the whole
 * reason this is a separate function.
 *
 * Unlike `FileRef`, `destinationServerRelPath` has no single encoding across its callers.
 * Some hand over a decoded path; others derive it from a `webUrl` pathname, which is
 * percent-encoded (see SharePointDocumentLibraryItem's drop handler, and the OneDrive
 * branch of DroppableBreadcrumbItem). Escaping `%` would turn one of those `%20`s into
 * `%2520` and point the copy job at a folder that does not exist — breaking a move into
 * any library or site whose name contains a space.
 *
 * `#` and `?` are safe to escape either way: in a percent-encoded path they would already
 * be `%23`/`%3F`, so a literal one can only ever be a real character in a folder name —
 * exactly the truncation this prevents. A destination containing neither is byte-identical
 * to the raw concatenation this replaced.
 */
export const escapeDestinationPathChars = (path: string): string =>
    path.replace(/[#?]/g, char => URL_UNSAFE_PATH_CHARS[char]);

/** Tenant origin of the source site, without a trailing slash. `""` when unparseable. */
export const resolveCopyJobSourceOrigin = (sourceSiteUrl?: string): string => {
    try {
        const origin = new URL(String(sourceSiteUrl ?? "").trim()).origin;
        return origin.endsWith("/") ? origin.slice(0, -1) : origin;
    } catch {
        return "";
    }
};

export const buildCopyJobSourcePaths = (sourceSiteUrl: string, files: any[]): ICopyJobSourcePathsResult => {
    const origin = resolveCopyJobSourceOrigin(sourceSiteUrl);
    const paths: ICopyJobSourcePath[] = [];
    const unresolved: any[] = [];

    // No usable origin means no file can be resolved. Report them all rather than
    // returning an empty-but-successful batch.
    if (!origin) {
        return { paths, unresolved: [...(files ?? [])] };
    }

    (files ?? []).forEach(file => {
        const rawFileRef = file?.FileRef;
        const serverRelativePath: string = typeof rawFileRef === "string" ? rawFileRef.trim() : "";
        if (!serverRelativePath) {
            unresolved.push(file);
            return;
        }

        const normalizedPath = serverRelativePath.startsWith("/") ? serverRelativePath : `/${serverRelativePath}`;

        let filePath: string;
        try {
            filePath = new URL(origin + escapeUrlUnsafePathChars(normalizedPath)).href;
        } catch {
            unresolved.push(file);
            return;
        }

        paths.push({
            filePath,
            containerPath: filePath.split("/").slice(0, -1).join("/"),
        });
    });

    return { paths, unresolved };
};
